// Story 7.1 (ADO-904) — the Department › Category › Sub-Category drill-state,
// extracted so it can be shared by the standalone `Browse` screen and the
// `/products` page (Epic 7 re-skin, ADO-903), which renders the same nav as a
// header mega-menu (BrowseNav) beside the product listing (CategoryListing).
//
// Selecting (or breadcrumb-navigating to) a node reconciles the whole drill from
// that node's level so the nav columns never contradict the listing: a DEPARTMENT
// resets the Category drill; a CATEGORY sets it; a SUBCATEGORY leaves both
// ancestors intact. Hover/focus REVEALS a column (expandNode) without selecting.

import { useCallback, useState } from 'react';
import type { CategoryNode } from '../../api/browse.client';

export interface BrowseDrill {
  readonly activeDepartment: CategoryNode | null;
  readonly activeCategory: CategoryNode | null;
  readonly selectedNode: CategoryNode | null;
  readonly selectNode: (node: CategoryNode) => void;
  readonly expandNode: (node: CategoryNode) => void;
  readonly navigate: (node: CategoryNode | null) => void;
  /**
   * Story 7.35 (ADO-2032): reconcile the whole drill from a breadcrumb trail
   * (Department → … → node, root-first) returned by a successful node listing.
   * A deep link / reload / Back arrives with only the node UUID in the URL, so
   * the drill starts empty; once the listing resolves its breadcrumb, this
   * restores the active Department, Category and selected node together, so the
   * mega-menu columns and highlighting match the listing. An empty trail (the
   * ROOT listing) collapses the drill.
   */
  readonly hydrate: (breadcrumb: readonly CategoryNode[]) => void;
}

export function useBrowseDrill(): BrowseDrill {
  const [activeDepartment, setActiveDepartment] = useState<CategoryNode | null>(null);
  const [activeCategory, setActiveCategory] = useState<CategoryNode | null>(null);
  const [selectedNode, setSelectedNode] = useState<CategoryNode | null>(null);

  // Hover/focus a Department or Category to reveal its column WITHOUT selecting it
  // (mega-menu drill) — no product listing loads.
  const expandNode = useCallback((node: CategoryNode) => {
    if (node.level === 'DEPARTMENT') {
      setActiveDepartment(node);
      setActiveCategory(null);
    } else if (node.level === 'CATEGORY') {
      setActiveCategory(node);
    }
  }, []);

  const selectNode = useCallback(
    (node: CategoryNode) => {
      expandNode(node);
      setSelectedNode(node);
    },
    [expandNode],
  );

  const navigate = useCallback(
    (node: CategoryNode | null) => {
      if (node === null) {
        // "Shop" root — collapse the whole drill-down.
        setActiveDepartment(null);
        setActiveCategory(null);
        setSelectedNode(null);
        return;
      }
      selectNode(node);
    },
    [selectNode],
  );

  // Story 7.35 (ADO-2032): restore the drill from a resolved breadcrumb so a
  // deep-linked / reloaded / Back-navigated listing shows the right Department,
  // Category and selection. The last breadcrumb element is the selected node;
  // the Department and Category come from their levels in the trail. An empty
  // trail (root listing) collapses the drill.
  const hydrate = useCallback((breadcrumb: readonly CategoryNode[]) => {
    setActiveDepartment(breadcrumb.find((n) => n.level === 'DEPARTMENT') ?? null);
    setActiveCategory(breadcrumb.find((n) => n.level === 'CATEGORY') ?? null);
    setSelectedNode(breadcrumb.length > 0 ? (breadcrumb[breadcrumb.length - 1] ?? null) : null);
  }, []);

  // (2026-07-17 demo feedback) The former `autoLoadFirstDepartment` opt-in is
  // gone: the root state (no selection) now IS a populated listing — the ROOT
  // all-products page — so no default Department is ever preselected.

  return {
    activeDepartment,
    activeCategory,
    selectedNode,
    selectNode,
    expandNode,
    navigate,
    hydrate,
  };
}
